import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { COMPANY } from "@/i18n/translations";
import { MapPin, Phone, Mail, ArrowRight, Clock, MessageCircle, Globe, Navigation, Building2 } from "lucide-react";

const MAPS_URL =
  "https://www.google.com/maps/dir/?api=1&destination=DNA+Automotive+GmbH,+Butzweilerstra%C3%9Fe+35-39,+50829+K%C3%B6ln";

export default function ContactSection({ prefillVehicle = "" }) {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", vehicle: prefillVehicle, website: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setForm((f) => ({ ...f, vehicle: prefillVehicle }));
  }, [prefillVehicle]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", subject: "", message: "", vehicle: prefillVehicle, website: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t.contact.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 rounded-none focus:border-brand-light focus:outline-none text-white placeholder-zinc-600 py-3 transition-colors";

  const rows = [
    {
      icon: Building2,
      testid: "contact-company",
      content: (
        <div className="text-zinc-200">
          <div className="font-semibold text-white">{COMPANY.name}</div>
          <div className="text-sm text-zinc-400">{COMPANY.location}</div>
        </div>
      ),
    },
    {
      icon: MapPin,
      testid: "contact-address",
      content: (
        <div className="text-zinc-200">
          {COMPANY.addressLine}<br />{COMPANY.city}
        </div>
      ),
    },
    {
      icon: Phone,
      testid: "contact-phone-row",
      content: <a href={`tel:${COMPANY.phoneHref}`} className="text-zinc-200 hover:text-brand-bright transition-colors">{COMPANY.phone}</a>,
    },
    {
      icon: Mail,
      testid: "contact-mail-row",
      content: <a href={`mailto:${COMPANY.email}`} className="text-zinc-200 hover:text-brand-bright transition-colors">{COMPANY.email}</a>,
    },
    {
      icon: Globe,
      testid: "contact-web-row",
      content: <a href="https://dna-automotive.de" target="_blank" rel="noopener noreferrer" className="text-zinc-200 hover:text-brand-bright transition-colors">dna-automotive.de</a>,
    },
    {
      icon: MessageCircle,
      testid: "contact-whatsapp-row",
      content: (
        <a
          href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent("Hallo DNA Automotive, ich habe eine Frage.")}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-zinc-200 hover:text-brand-bright transition-colors"
        >
          WhatsApp
        </a>
      ),
    },
  ];

  return (
    <section id="contact" className="py-28 px-6 md:px-10 bg-obsidian-surface border-t border-white/10" data-testid="contact-section">
      <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
        <div>
          <p className="text-brand-bright text-xs uppercase tracking-[0.3em] mb-4">{t.contact.overline}</p>
          <h2 className="font-heading font-black uppercase tracking-tighter text-white text-4xl md:text-6xl leading-[0.95] mb-6">
            {t.contact.title}
          </h2>
          <p className="text-zinc-400 max-w-md mb-10">{t.contact.subtitle}</p>

          <div className="bg-black/60 backdrop-blur-2xl border border-white/10 p-7 md:p-8">
            <ul className="space-y-5">
              {rows.map(({ icon: Icon, testid, content }) => (
                <li key={testid} className="flex items-start gap-4" data-testid={testid}>
                  <Icon className="w-5 h-5 text-brand-bright mt-0.5 shrink-0" />
                  {content}
                </li>
              ))}
              <li className="flex items-start gap-4" data-testid="contact-hours">
                <Clock className="w-5 h-5 text-brand-bright mt-0.5 shrink-0" />
                <div className="text-zinc-200">
                  <div className="font-semibold text-white mb-1">{t.contact.openingLabel}</div>
                  <ul className="space-y-0.5 text-sm">
                    {COMPANY.hours.map((h) => (
                      <li key={h.day} className="flex gap-3">
                        <span className="text-zinc-400 w-24">{h.day}</span>
                        <span>{h.time}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            </ul>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-8 pt-7 border-t border-white/10">
              <a
                href={`tel:${COMPANY.phoneHref}`}
                data-testid="contact-call-button"
                className="inline-flex items-center justify-center gap-2 bg-brand text-black text-xs font-bold uppercase tracking-wider px-4 py-3.5 hover:bg-brand-hover transition-colors"
              >
                <Phone className="w-4 h-4" />
                {t.contact.call}
              </a>
              <a
                href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent("Hallo DNA Automotive, ich habe eine Frage.")}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-button"
                className="inline-flex items-center justify-center gap-2 border border-white/20 text-white text-xs font-bold uppercase tracking-wider px-4 py-3.5 hover:border-brand-light hover:text-brand-bright transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </a>
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-route-button"
                className="inline-flex items-center justify-center gap-2 border border-white/20 text-white text-xs font-bold uppercase tracking-wider px-4 py-3.5 hover:border-brand-light hover:text-brand-bright transition-colors"
              >
                <Navigation className="w-4 h-4" />
                {t.contact.route}
              </a>
            </div>
          </div>
        </div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="space-y-6"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <input name="name" value={form.name} onChange={onChange} required placeholder={t.contact.name} className={inputCls} data-testid="contact-name" />
            <input name="email" type="email" value={form.email} onChange={onChange} required placeholder={t.contact.email} className={inputCls} data-testid="contact-email" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <input name="phone" value={form.phone} onChange={onChange} placeholder={t.contact.phone} className={inputCls} data-testid="contact-phone" />
            <input name="subject" value={form.subject} onChange={onChange} placeholder={t.contact.subject} className={inputCls} data-testid="contact-subject" />
          </div>
          {prefillVehicle && (
            <input name="vehicle" value={form.vehicle} onChange={onChange} placeholder="Fahrzeug" className={inputCls} data-testid="contact-vehicle" />
          )}
          <textarea name="message" value={form.message} onChange={onChange} required rows={4} placeholder={t.contact.message} className={inputCls + " resize-none"} data-testid="contact-message" />
          {/* Honeypot: unsichtbar für Menschen, Bots füllen es aus */}
          <input
            type="text"
            name="website"
            value={form.website}
            onChange={onChange}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="hidden"
            data-testid="contact-honeypot"
          />
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit"
            className="group inline-flex items-center gap-2 bg-brand text-black font-bold uppercase tracking-wider text-sm px-8 py-4 hover:bg-brand-hover transition-colors disabled:opacity-50"
          >
            {sending ? t.contact.sending : t.contact.send}
            {!sending && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
